import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { getCalcProject, listCalcSheets } from "../../api/calc";
import type { CalcProject, CalcSheet } from "../../types/calc";
import {
  CalcProjectNav,
  CalcProjectNavDrawer,
  CalcProjectNavTrigger,
  type CalcProjectNavSection,
} from "./CalcProjectNav";
import {
  cacheCalcProject,
  cacheCalcSheets,
  getCachedCalcProject,
  getCachedCalcSheets,
} from "./calcProjectStore";
import { PRODUCT_SIDEBAR_SLOT_ID } from "../shell/shellSlots";
import { CalcExportProvider } from "./export/CalcExportWizard";

type CalcProjectContextValue = {
  projectId: string;
  project: CalcProject | null;
  sheets: CalcSheet[];
  loading: boolean;
  setProject: Dispatch<SetStateAction<CalcProject | null>>;
  setSheets: Dispatch<SetStateAction<CalcSheet[]>>;
};

const CalcProjectContext = createContext<CalcProjectContextValue | null>(null);

function CalcProjectProvider({ children }: { children: ReactNode }) {
  const { id: projectId = "" } = useParams<{ id: string }>();
  const cachedProject = getCachedCalcProject(projectId);
  const cachedSheets = getCachedCalcSheets(projectId);
  const [projectState, setProjectState] = useState<CalcProject | null>(cachedProject);
  const [sheetState, setSheetState] = useState<CalcSheet[]>(cachedSheets ?? []);
  const [loading, setLoading] = useState(!cachedProject || !cachedSheets);

  const setProject: Dispatch<SetStateAction<CalcProject | null>> = useCallback((next) => {
    setProjectState((current) => {
      const resolved = typeof next === "function" ? next(current) : next;
      cacheCalcProject(projectId, resolved);
      return resolved;
    });
  }, [projectId]);

  const setSheets: Dispatch<SetStateAction<CalcSheet[]>> = useCallback((next) => {
    setSheetState((current) => {
      const resolved = typeof next === "function" ? next(current) : next;
      cacheCalcSheets(projectId, resolved);
      return resolved;
    });
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const hasCachedShell = !!getCachedCalcProject(projectId) && !!getCachedCalcSheets(projectId);
    if (!hasCachedShell) setLoading(true);

    Promise.allSettled([getCalcProject(projectId), listCalcSheets(projectId)])
      .then(([projectResult, sheetsResult]) => {
        if (!alive) return;
        if (projectResult.status === "fulfilled") setProject(projectResult.value);
        if (sheetsResult.status === "fulfilled") setSheets(sheetsResult.value.calculations);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [projectId, setProject, setSheets]);

  const value = useMemo<CalcProjectContextValue>(() => ({
    projectId,
    project: projectState,
    sheets: sheetState,
    loading,
    setProject,
    setSheets,
  }), [projectId, projectState, sheetState, loading, setProject, setSheets]);

  return <CalcProjectContext.Provider value={value}>{children}</CalcProjectContext.Provider>;
}

export function useCalcProject() {
  const value = useContext(CalcProjectContext);
  if (!value) throw new Error("useCalcProject must be used inside CalcProjectLayout");
  return value;
}

function CalcProjectFrame() {
  const { project, sheets } = useCalcProject();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarHost, setSidebarHost] = useState<HTMLElement | null>(null);
  const isPreview = location.pathname.startsWith("/calc-preview/");
  const memberMatch = location.pathname.match(/\/(?:m|c|l)\/([^/]+)$/);
  const loadDraftOpen = /\/load\/[^/]+$/.test(location.pathname);
  const activeSheetId = memberMatch?.[1] ?? null;
  const activeNav: CalcProjectNavSection = location.pathname.endsWith("/report-details")
    ? "report"
    : location.pathname.endsWith("/add")
      ? "add"
      : activeSheetId || loadDraftOpen
        ? "none"
        : "viewer";

  useLayoutEffect(() => {
    if (isPreview) {
      setSidebarHost(null);
      return;
    }
    setSidebarHost(document.getElementById(PRODUCT_SIDEBAR_SLOT_ID));
  }, [isPreview]);

  const projectNav = (
    <CalcProjectNav
      project={project}
      sheets={sheets}
      activeNav={activeNav}
      activeSheetId={activeSheetId}
    />
  );

  return (
    <>
      {sidebarHost && createPortal(projectNav, sidebarHost)}
      <div className="calc-project-layout">
        <div className="calc-project-mobile-nav">
          <CalcProjectNavTrigger onClick={() => setMobileNavOpen(true)} />
        </div>
        <div className="calc-project-body">
          {isPreview && (
            <aside className="cs-sidebar calc-project-sidebar" aria-label="Calc project navigation">
              {projectNav}
            </aside>
          )}
          <div className="calc-project-route-body">
            <Outlet />
          </div>
        </div>
        <CalcProjectNavDrawer
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          project={project}
          sheets={sheets}
          activeNav={activeNav}
          activeSheetId={activeSheetId}
        />
      </div>
    </>
  );
}

export function CalcProjectLayout() {
  const { id: projectId = "" } = useParams<{ id: string }>();

  return (
    <CalcProjectProvider key={projectId}>
      <CalcExportProvider>
        <CalcProjectFrame />
      </CalcExportProvider>
    </CalcProjectProvider>
  );
}
